import {
  AFFILIATION_FILTER_GROUP_IDS,
  FLAT_FILTER_GROUP_IDS,
  affiliationValues,
  allAffiliationSelection,
  allFilterSelection,
  emptyAffiliationValues,
  flatSelectionValues,
  isSelectionAll,
  normalizeAffiliationValues,
  normalizeCalendarFilters,
} from './calendar-filter-state';

const VIEW_TO_URL = { list: 'list', calendar: 'calendar', categories: 'grid' };
const URL_TO_VIEW = { list: 'list', calendar: 'calendar', grid: 'categories' };

// Product Calendar URLs encode spaces as %2B (literal '+' that a form-decoder reads as space).
// Keep that byte-for-byte contract so existing bookmarks continue to resolve.
function enc(value) {
  return encodeURIComponent(String(value)).replace(/%20/g, '%2B');
}

function pairString([key, value]) {
  return `${enc(key)}=${enc(value)}`;
}

function decodeFilterValue(value) {
  return String(value).replace(/\+/g, ' ');
}

function filterValues(params, key) {
  return [
    ...params.getAll(`${key}[]`),
    ...params.getAll(key).flatMap(value => String(value).split(',')),
  ]
    .map(decodeFilterValue)
    .filter(value => value !== '');
}

function uniqueValues(values) {
  return [...new Set(values)];
}

function isPristine(filters) {
  if (!filters) return true;
  const normalized = normalizeCalendarFilters(filters);

  return [...FLAT_FILTER_GROUP_IDS, 'affiliations'].every(group =>
    isSelectionAll(normalized[group])
  );
}

// "Pristine" form: no filter group has a specific selection.
// Output is fully alphabetical and uses `filters[<group>]=` bare keys plus `=true`/`=false` flags.
function serializePristine({ dstart, view, includes_past_events }) {
  const pairs = [['dstart', dstart || '']];

  for (const group of FLAT_FILTER_GROUP_IDS) {
    pairs.push([`filters[${group}]`, '']);
  }
  pairs.push(['filters[affiliations]', '']);

  for (const group of FLAT_FILTER_GROUP_IDS) {
    pairs.push([`filters[all_${group}]`, 'true']);
  }
  pairs.push(['filters[all_affiliations]', 'true']);

  if (includes_past_events) {
    pairs.push(['include_past_events', 'true']);
  }
  pairs.push(['includes_past_events', 'false']);
  pairs.push(['view_option', view]);

  pairs.sort((a, b) => a[0].localeCompare(b[0]));
  return pairs.map(pairString).join('&');
}

// "Modified" form: at least one filter group has a specific selection.
// Top section (dstart, [include_past_events,] includes_past_events, view_option) then
// all `filters[*]` pairs sorted alphabetically, using the real backend affiliation buckets.
function serializeModified({ dstart, view, includes_past_events, filters }) {
  const normalized = normalizeCalendarFilters(filters);
  const top = [['dstart', dstart || '']];
  if (includes_past_events) top.push(['include_past_events', 'true']);
  top.push(['includes_past_events', 'false']);
  top.push(['view_option', view]);

  const filterPairs = [];

  for (const group of FLAT_FILTER_GROUP_IDS) {
    const selected = flatSelectionValues(normalized[group]);
    const isAll = isSelectionAll(normalized[group]);
    filterPairs.push([`filters[${group}][]`, '']);
    if (!isAll) {
      for (const v of selected) filterPairs.push([`filters[${group}][]`, v]);
    }
    filterPairs.push([`filters[all_${group}]`, isAll ? '1' : '0']);
  }

  const affSelected = affiliationValues(normalized.affiliations);
  const affIsAll = isSelectionAll(normalized.affiliations);

  for (const group of AFFILIATION_FILTER_GROUP_IDS) {
    filterPairs.push([`filters[affiliations][${group}][]`, '']);
  }

  if (!affIsAll) {
    for (const group of AFFILIATION_FILTER_GROUP_IDS) {
      for (const id of affSelected[group]) {
        filterPairs.push([`filters[affiliations][${group}][]`, id]);
      }
    }
  }
  filterPairs.push(['filters[all_affiliations]', affIsAll ? '1' : '0']);

  filterPairs.sort((a, b) => a[0].localeCompare(b[0]));
  return [...top, ...filterPairs].map(pairString).join('&');
}

export function serializeCalendarState({ dstart, view, includes_past_events, filters }) {
  const viewValue = VIEW_TO_URL[view] || view || 'list';
  const args = { dstart, view: viewValue, includes_past_events, filters };
  return isPristine(filters) ? serializePristine(args) : serializeModified(args);
}

// Returns true if any value of the all_<group> key indicates "all selected".
// Handles the reference's duplicate-key pattern (`=0` then `=1`) and the pristine `=true`.
function allFlagOn(params, key) {
  const values = params.getAll(key);
  if (values.length === 0) return null; // signal "absent"
  return values.some(v => v === 'true' || v === '1');
}

export function parseCalendarState(queryString) {
  const params = new URLSearchParams(queryString || '');
  const state = {
    dstart: null,
    view: null,
    includes_past_events: false,
    filters: {},
  };

  if (params.has('dstart')) state.dstart = params.get('dstart');

  if (params.has('view_option')) {
    const v = params.get('view_option');
    state.view = URL_TO_VIEW[v] || v;
  }

  state.includes_past_events = params.get('include_past_events') === 'true';

  for (const group of FLAT_FILTER_GROUP_IDS) {
    const isAll = allFlagOn(params, `filters[all_${group}]`);
    if (isAll === null || isAll) {
      state.filters[group] = allFilterSelection();
    } else {
      state.filters[group] = {
        all: false,
        values: uniqueValues(filterValues(params, `filters[${group}]`)),
      };
    }
  }

  const affIsAll = allFlagOn(params, 'filters[all_affiliations]');
  if (affIsAll === null || affIsAll) {
    state.filters.affiliations = allAffiliationSelection();
  } else {
    const aff = emptyAffiliationValues();
    const affKeyRegex = /^filters\[affiliations\]\[([^\]]+)\]\[\]$/;
    for (const [key, value] of params.entries()) {
      const match = key.match(affKeyRegex);
      if (match && value !== '' && AFFILIATION_FILTER_GROUP_IDS.includes(match[1])) {
        aff[match[1]].push(decodeFilterValue(value));
      }
    }

    const normalized = normalizeAffiliationValues(aff);
    const hasSelectedAffiliations = Object.values(normalized).some(values => values.length > 0);
    state.filters.affiliations = hasSelectedAffiliations
      ? { all: false, values: normalized }
      : allAffiliationSelection();
  }

  return state;
}

function isDateKey(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function monthValuesFromRanges(months) {
  return (months || [])
    .map(month => (typeof month === 'string' ? month : month?.start_after))
    .filter(value => typeof value === 'string' && isDateKey(value));
}

function academicFilterGroupValues(params, group) {
  const isAll = allFlagOn(params, `filters[all_${group}]`);
  const values = filterValues(params, `filters[${group}]`);

  if (isAll === null && values.length === 0) {
    return null;
  }

  return isAll === true
    ? allFilterSelection()
    : {
        all: false,
        values: uniqueValues(values),
      };
}

function academicAffiliationValues(params) {
  const isAll = allFlagOn(params, 'filters[all_affiliations]');
  const values = emptyAffiliationValues();
  const affKeyRegex = /^filters\[affiliations\]\[([^\]]+)\]\[\]$/;

  for (const [key, value] of params.entries()) {
    const match = key.match(affKeyRegex);
    if (match && value !== '' && AFFILIATION_FILTER_GROUP_IDS.includes(match[1])) {
      values[match[1]].push(decodeFilterValue(value));
    }
  }

  const normalized = normalizeAffiliationValues(values);
  const hasSelectedAffiliations = Object.values(normalized).some(group => group.length > 0);

  if (isAll === null && !hasSelectedAffiliations) {
    return null;
  }

  return isAll === true || !hasSelectedAffiliations
    ? allAffiliationSelection()
    : { all: false, values: normalized };
}

export function serializeAcademicCalendarState({ academicYear, months, filters }) {
  const normalized = normalizeCalendarFilters(filters);
  const pairs = [
    ['filters[months][]', ''],
    ...monthValuesFromRanges(months).map(month => ['filters[months][]', month]),
  ];

  // The URL param is `school_year` (which means `academic_year`): it is the legacy Rails contract and old bookmarks may carry it.
  if (academicYear) {
    pairs.push(['filters[school_year]', academicYear]);
  }

  for (const group of FLAT_FILTER_GROUP_IDS) {
    const selected = flatSelectionValues(normalized[group]);
    const isAll = isSelectionAll(normalized[group]);

    pairs.push([`filters[${group}][]`, '']);
    if (!isAll) {
      for (const value of selected) pairs.push([`filters[${group}][]`, value]);
    }
    pairs.push([`filters[all_${group}]`, isAll ? '1' : '0']);
  }

  const affSelected = affiliationValues(normalized.affiliations);
  const affIsAll = isSelectionAll(normalized.affiliations);

  for (const group of AFFILIATION_FILTER_GROUP_IDS) {
    pairs.push([`filters[affiliations][${group}][]`, '']);
  }

  if (!affIsAll) {
    for (const group of AFFILIATION_FILTER_GROUP_IDS) {
      for (const id of affSelected[group]) {
        pairs.push([`filters[affiliations][${group}][]`, id]);
      }
    }
  }
  pairs.push(['filters[all_affiliations]', affIsAll ? '1' : '0']);

  pairs.sort((a, b) => a[0].localeCompare(b[0]));
  return pairs.map(pairString).join('&');
}

export function parseAcademicCalendarState(queryString) {
  const params = new URLSearchParams(queryString || '');
  const academicYear = parseInt(params.get('filters[school_year]'), 10);
  const state = {
    academicYear: Number.isNaN(academicYear) ? null : academicYear,
    months: uniqueValues(filterValues(params, 'filters[months]').filter(isDateKey)),
    filters: {},
  };

  for (const group of FLAT_FILTER_GROUP_IDS) {
    const values = academicFilterGroupValues(params, group);
    if (values !== null) {
      state.filters[group] = values;
    }
  }

  const affiliations = academicAffiliationValues(params);
  if (affiliations !== null) {
    state.filters.affiliations = affiliations;
  }

  return state;
}
